class StickyATC extends HTMLElement {
  constructor() {
    super();
    this.initialize();
  }

  initialize() {
    // Clean up any existing event listeners
    this.cleanup();

    // Initialize properties
    this.originalForm = document.querySelector('.product product-form');
    this.productInfoContainer = document.querySelector('.product .product__info-container');
    this.productImage = document.querySelector('.product .product__media-gallery img');
    this.mainPriceElement = document.querySelector('.product .product-price');
    this.atcButton = this.querySelector('.sticky-atc__button');
    this.variantSelectContainer = this.querySelector('.sticky-atc__variant-container');
    this.priceContainer = this.querySelector('.sticky-atc__price');
    this.productImageElement = this.querySelector('.sticky-atc__image img');
    this.titleElement = this.querySelector('.sticky-atc__title');
    this.threshold = 200;
    this.isVisible = false;

    if (!this.originalForm) return;

    // Initialize components
    this.initializeWindowScrollListenner();
    this.initializeResizeListener();
    this.initializeVariantPicker();
    this.initializeButtonATC();
    this.updateVisibility();
  }

  // Clean up event listeners to prevent memory leaks
  cleanup() {
    window.removeEventListener('scroll', this.boundUpdateVisibility);
    window.removeEventListener('resize', this.boundUpdateBodyPadding);

    if (this.atcButton) {
      this.atcButton.removeEventListener('click', this.boundHandleAtcClick);
    }

    document.body.classList.remove('has-sticky-atc');
    document.body.style.removeProperty('--sticky-atc-height');

    // Reset button state if it was in loading state
    this.resetButtonState();
  }

  initializeResizeListener() {
    this.boundUpdateBodyPadding = () => this.updateBodyPadding();
    window.addEventListener('resize', this.boundUpdateBodyPadding);
  }

  updateBodyPadding() {
    if (!this.isVisible) return;
    const height = this.offsetHeight;
    if (height > 0) {
      document.body.style.setProperty('--sticky-atc-height', height + 'px');
    }
  }

  initializeWindowScrollListenner() {
    // Store bound function for later removal
    this.boundUpdateVisibility = this.updateVisibility.bind(this);

    // Listen for scroll events to show/hide the sticky ATC
    window.addEventListener('scroll', this.boundUpdateVisibility);
  }

  initializeButtonATC() {
    if (!this.atcButton) return;

    // Store bound function for later removal
    this.boundHandleAtcClick = this.handleAtcClick.bind(this);

    // Listen for clicks on the ATC button
    if (this.atcButton) {
      this.atcButton.addEventListener('click', this.boundHandleAtcClick);
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, this.resetButtonState.bind(this));
  }

  initializeVariantPicker() {
    if (!this.variantSelectContainer) return;

    // Set up event listener for variant selection changes in the sticky form
    const variantInputs = this.variantSelectContainer.querySelectorAll('input[type="radio"]');
    variantInputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        this.handleStickyVariantChange(event, variantInputs);
      });
    });
  }

  handleStickyVariantChange(event, variantInputs) {
    // Get the selected variant input
    const selectedVariantInput = event.target;

    // Update UI in the sticky form
    this.updateStickyVariantUI(selectedVariantInput, variantInputs);

    // Get main variant selects and fieldsets
    this.mainVariantSelects = document.querySelector('.product variant-selects');
    const mainOptionFieldsets = this.mainVariantSelects.querySelectorAll('fieldset');
    if (!mainOptionFieldsets.length) return;

    // Get option values from the selected variant
    const optionValues = this.getOptionValuesFromVariant(selectedVariantInput);

    // Update main form inputs
    this.updateMainFormInputs(mainOptionFieldsets, optionValues);
  }

  updateStickyVariantUI(selectedVariantInput, variantInputs) {
    // Reset all inputs
    variantInputs.forEach((input) => {
      input.checked = false;
      input.closest('label').classList.remove('variant-picker-container__modal-body--option-selected');
    });

    // Check the selected variant input
    selectedVariantInput.checked = true;
    selectedVariantInput.closest('label').classList.add('variant-picker-container__modal-body--option-selected');
  }

  getOptionValuesFromVariant(variantInput) {
    const optionValues = [];
    let optionIndex = 1;

    // Collect all option values from data attributes
    while (variantInput.hasAttribute(`data-option-${optionIndex}`)) {
      optionValues.push(variantInput.getAttribute(`data-option-${optionIndex}`));
      optionIndex++;
    }

    return optionValues;
  }

  updateMainFormInputs(fieldsets, optionValues) {
    // For each option, find and select the corresponding input in the main form
    fieldsets.forEach((fieldset, index) => {
      if (index < optionValues.length) {
        const optionValue = optionValues[index];

        // Find the input in the main form with this option value
        const mainInput = fieldset.querySelector(`input[type="radio"][value="${optionValue}"]`);

        if (mainInput) {
          mainInput.checked = true;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  handleAtcClick() {
    // Set button to loading state
    this.setButtonLoading();

    // Trigger add to cart on the main form
    const submitButton = this.originalForm.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.click();
    }
  }

  setButtonLoading() {
    if (!this.atcButton) return;

    // Disable the button
    this.atcButton.setAttribute('aria-disabled', 'true');

    // Add loading class
    this.atcButton.classList.add('loading');

    // Show loading spinner
    const spinner = this.atcButton.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
  }

  resetButtonState() {
    if (!this.atcButton) return;

    // Remove loading class
    this.atcButton.classList.remove('loading');

    // Enable the button
    this.atcButton.removeAttribute('aria-disabled');

    // Hide loading spinner
    const spinner = this.atcButton.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  updateVisibility() {
    const atcRect = this.originalForm ? this.originalForm.getBoundingClientRect() : null;
    const isOcareArticulations = Boolean(document.querySelector('.product-info--ocare-refresh'));
    const shouldShow = atcRect
      ? isOcareArticulations
        ? atcRect.bottom < 96
        : atcRect.top < this.threshold
      : false;

    if (shouldShow && !this.isVisible) {
      this.classList.add('sticky-atc--visible');
      this.isVisible = true;
      document.body.classList.add('has-sticky-atc');
      requestAnimationFrame(() => this.updateBodyPadding());
    } else if (!shouldShow && this.isVisible) {
      this.classList.remove('sticky-atc--visible');
      this.isVisible = false;
      document.body.classList.remove('has-sticky-atc');
    }
  }
}

customElements.define('sticky-atc', StickyATC);
